import { QrCode, RefreshCw } from 'lucide-react';
import { useState, useEffect, useContext } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

import api from '../../utils/api';
import { AuthContext } from '../../context/AuthContext';
import defaultVerticalAd from '../../assets/fitscan_vertical_ad.png';
import API_BASE_URL from '../../config';





const QRCodeContent = ({ activeGymcode, gyms, handleSwitchGym }) => {
  const { user } = useContext(AuthContext);
  const [qrToken, setQrToken] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [qrSize, setQrSize] = useState(200);
  const [showTooltip, setShowTooltip] = useState(false);
  const [qrError, setQrError] = useState(null);
  const [qrGymDropdownOpen, setQrGymDropdownOpen] = useState(false);
  const [expiredState, setExpiredState] = useState({ isExpired: false, expiryDate: null });

  const [adImageUrl, setAdImageUrl] = useState(defaultVerticalAd);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleAdTap = () => {
    setIsModalOpen(true);

    // Fire-and-forget — don't await, don't block UI
    const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token || '';
    fetch(`${API_BASE_URL}/api/gym/ad-impression`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    }).catch(() => { }); // Silent fail — tracking must never break the ad
  };

  useEffect(() => {
    const fetchAdBanner = async () => {
      try {
        const { data } = await api.get('/api/settings/fitscan-ad');
        if (data.success && data.imageUrl && !data.imageUrl.includes("Fitscan_ads_block_wide")) {
          setAdImageUrl(data.imageUrl);
        }
      } catch (err) {
        console.warn("Failed to load custom ad banner, using default.", err);
      }
    };
    fetchAdBanner();
  }, []);

  const checkStatus = async () => {
    try {
      if (!user?.username) return false;
      const { data } = await api.get(`/api/qrcode/activecheckin/${user.username}`);
      if (data.success && data.isCheckedIn) {
        if (data.checkintime) {
          const checkinTime = new Date(data.checkintime.endsWith('Z')
            ? data.checkintime : data.checkintime + 'Z');
          const hours = (Date.now() - checkinTime.getTime()) / 3600000;
          if (hours > 12) { await handleCheckout(); return false; }
        }
        setIsCheckedIn(true);
        return true;
      } else {
        setIsCheckedIn(false);
        return false;
      }
    } catch (err) {
      console.error('checkStatus error:', err);
      setIsCheckedIn(false);
      return false;
    }
  };


  const handleCheckout = async () => {
    if (!user?.username) return;

    try {
      const { data } = await api.post(`/api/qrcode/checkout/${user.username}`);
      if (data.success) {
        setIsCheckedIn(false);
        setQrError(null);
        generateQRCode();
      } else {
        setQrError(data.message || 'Checkout failed.');
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setQrError("An error occurred while checking out.");
    }
  };



  const generateQRCode = async () => {
    if (!user?.username) return;
    try {
      setIsRefreshing(true);
      setQrError(null);
      setExpiredState({ isExpired: false, expiryDate: null });

      let data;
      if (user.role === 'Owner' || user.role === 'Trainer') {
        const result = await api.post('/api/qrcode/OQRgenerate', {
          username: user.username, gymcode: user.gymcode
        });
        data = result.data;
      } else {
        const result = await api.post('/api/qrcode/generate', {
          username: user.username, lat: 0, lon: 0, gymcode: activeGymcode
        });
        data = result.data;
      }
      if (!data || !data.success) {
        setQrError(data?.message || 'Failed to generate QR code. Please try again.');
        setQrToken(null);
      } else {
        setQrToken(data.hmc_string);
        setQrError(null);
      }
    } catch (error) {
      console.error('QR Code generation failed:', error);
      if (error.response?.data?.status === 'expired') {
        setExpiredState({
          isExpired: true,
          expiryDate: error.response.data.expiryDate
        });
      } else {
        setQrError(error.response?.data?.message || 'An unexpected error occurred while generating the QR code.');
      }
    } finally {
      setIsRefreshing(false);
    }
  };
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 350) {
        setQrSize(160);
      } else {
        setQrSize(290);
      }
    };

    handleResize(); // Set initial size
    window.addEventListener("resize", handleResize); // 👈 listen for window resize

    return () => {
      window.removeEventListener("resize", handleResize); // clean up
    };
  }, []);

  useEffect(() => {
    if (!user?.username) return; // guard for late-loading auth
    const init = async () => {
      setExpiredState({ isExpired: false, expiryDate: null });
      const checkedIn = await checkStatus(); // await + use return value
      if (!checkedIn) {
        generateQRCode(); // only generate if NOT already checked in
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username, activeGymcode]); // re-runs once when user first becomes available or activeGymcode changes

  useEffect(() => {
    let intervalId = null;

    const startPolling = () => {
      if (!isCheckedIn && !intervalId) {
        intervalId = setInterval(checkStatus, 5000);
      }
    };

    const stopPolling = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        stopPolling();        // clear any stale interval
        checkStatus();        // one fresh check on return
        startPolling();       // restart clean
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    if (!isCheckedIn) startPolling();

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckedIn]);



  return (
    <div className="flex flex-col gap-1.5 md:gap-3">
      <div className="flex flex-col gap-1.5 md:gap-3">

        {/* Header Row: Icon + Title + Refresh all inline */}
        <div className="flex items-center gap-2 px-1 pt-0.5">
          <QrCode size={22} className="text-lime-400 flex-shrink-0" />
          <h1 className="text-xl font-bold">QR Code</h1>

          {/* Refresh + Tooltip inline, pushed to the right */}
          <div className="ml-auto relative group flex items-center gap-1.5">
            <button
              onClick={generateQRCode}
              disabled={isRefreshing}
              className="flex items-center gap-1 text-sm text-lime-400 hover:text-lime-300 transition-colors"
            >
              <RefreshCw size={15} className={isRefreshing ? 'animate-spin' : ''} />
              Refresh QR
            </button>

            {/* Info Icon */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowTooltip(!showTooltip);
              }}
              className="text-gray-500 hover:text-gray-400 transition-colors"
              aria-label="Show info"
            >
              <span className="text-xs font-serif italic border border-gray-600 rounded-full w-3.5 h-3.5 flex items-center justify-center">i</span>
            </button>

            {/* Tooltip */}
            <div className={`absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap shadow-lg transition-all duration-300 pointer-events-none z-20
              ${showTooltip ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0'}
            `}>
              Rotate daily for security.
              <div className="absolute top-full right-4 border-4 border-transparent border-t-gray-800"></div>
            </div>
          </div>
        </div>

        {/* QR Card */}
        {qrError && (
          <div className="bg-red-900/30 border border-red-500/40 text-red-300 text-sm rounded-lg px-4 py-3 mb-2 flex justify-between">
            <span>{qrError}</span>
            <button onClick={() => setQrError(null)} className="text-red-400 hover:text-red-200 ml-3 font-bold">✕</button>
          </div>
        )}
        <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-2 md:p-3">
          {expiredState.isExpired ? (
            <RenewalCard expiryDate={expiredState.expiryDate} activeGymcode={activeGymcode} gyms={gyms} />
          ) : (
            <>
              <div className="bg-white p-6 md:p-8 rounded-lg flex justify-center items-center">
                {isCheckedIn ? (
                  <div className="text-center py-4">
                    <h2 className="text-lime-500 font-semibold mb-3">✅ You are Checked In</h2>
                    <button
                      onClick={() => handleCheckout(false)}
                      className="bg-[#EF4444] hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
                    >
                      Check Out
                    </button>
                  </div>
                ) : isRefreshing ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <RefreshCw size={40} className="animate-spin text-lime-400 mb-4" />
                    <p className="text-gray-400">Generating Secure QR...</p>
                  </div>
                ) : qrToken ? (
                  <div className="mx-auto flex justify-center items-center">
                    <QRCodeCanvas value={qrToken} size={qrSize}
                      className="mx-auto w-48 h-48"
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8">
                    <p className="text-red-400 mb-4">Failed to load QR code</p>
                    <button
                      onClick={generateQRCode}
                      className="bg-lime-400 hover:bg-lime-300 text-[#181f10] px-6 py-2 rounded-lg font-semibold transition-colors shadow-sm hover:shadow-md"
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-2 text-xs md:text-sm text-gray-400 px-1">
                <p>• Scan this code at your gym entrance</p>
                <p>• Valid only for your registered gym</p>
              </div>
            </>
          )}

          {user?.role === 'User' && gyms && gyms.length > 0 && (
            <div className="mt-4 pt-3 border-t border-[#2b2b2b] flex items-center justify-between text-xs md:text-sm text-gray-300 px-1">
              <span className="flex items-center gap-1.5">
                <span className="text-lime-400">📍</span>
                Checking in at: <strong className="text-white font-semibold">{gyms.find(g => g.gymcode === activeGymcode)?.gymName || 'Select Gym'}</strong>
              </span>
              {gyms.length >= 2 && (
                <div className="relative">
                  <button
                    onClick={() => setQrGymDropdownOpen(!qrGymDropdownOpen)}
                    className="text-lime-400 hover:text-lime-300 font-bold text-xs bg-transparent border-0 cursor-pointer flex items-center gap-0.5"
                  >
                    Change ›
                  </button>
                  {qrGymDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-45" onClick={() => setQrGymDropdownOpen(false)} />
                      <div className="absolute right-0 bottom-full mb-2 w-56 rounded-xl bg-[#1c1c1c] border border-[#2b2b2b] shadow-2xl p-1.5 z-50 animate-scale-up">
                        {gyms.map((gym) => (
                          <button
                            key={gym.gymcode}
                            onClick={() => {
                              handleSwitchGym(gym.gymcode);
                              setQrGymDropdownOpen(false);
                            }}
                            className={`flex flex-col items-start w-full px-3 py-2 rounded-lg text-left border-0 cursor-pointer transition-colors ${
                              gym.gymcode === activeGymcode
                                ? 'bg-lime-950/30 text-lime-400'
                                : 'bg-transparent text-gray-300 hover:bg-[#282828] hover:text-white'
                            }`}
                          >
                            <span className="font-bold text-xs">{gym.gymName}</span>
                            <span className="text-[10px] text-gray-500 truncate w-full">{gym.location}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Brand / Ad Card */}
        <div
          onClick={handleAdTap}
          className="w-full aspect-[3/4] rounded-2xl overflow-hidden border border-[#2b2b2b] cursor-pointer hover:border-[#6CD800]/50 transition-all duration-300 relative group"
        >
          <img
            src={adImageUrl}
            alt="FitScan Advertisement"
            className="w-full h-full object-cover rounded-2xl hover:scale-[1.02] transition-transform duration-300"
          />
        </div>

        {/* Ad Full View Modal */}
        {isModalOpen && (
          <div
            onClick={() => setIsModalOpen(false)}
            className="fixed inset-0 z-[100] backdrop-blur-md bg-black/80 flex items-center justify-center p-4 cursor-pointer animate-fade-in"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-sm w-full aspect-[3/4] bg-[#181818] border border-[#2b2b2b] rounded-2xl overflow-hidden shadow-2xl animate-scale-up cursor-default"
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center border border-[#2b2b2b] hover:border-white transition-colors"
                aria-label="Close ad modal"
              >
                ✕
              </button>
              <img
                src={adImageUrl}
                alt="FitScan Advertisement Expanded"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

const RenewalCard = ({ expiryDate, activeGymcode, gyms }) => {
  const activeGym = gyms?.find(g => g.gymcode === activeGymcode);
  const rawPhone = activeGym?.phonenumber || activeGym?.phone || '';
  
  let formattedPhoneForWa = rawPhone.replace(/\D/g, '');
  if (formattedPhoneForWa.length === 10) {
    formattedPhoneForWa = '91' + formattedPhoneForWa;
  }
  
  const whatsappUrl = `https://wa.me/${formattedPhoneForWa}?text=Hi,%20I'd%20like%20to%20renew%20my%20FitScan%20membership.`;
  const callUrl = `tel:${rawPhone}`;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="flex flex-col items-center justify-center text-center p-6 md:p-8 bg-[#101010] border border-[#2b2b2b] rounded-lg space-y-5 shadow-inner">
      <div className="w-16 h-16 bg-red-500/10 border border-red-500/20 rounded-full flex items-center justify-center text-red-500 text-3xl animate-pulse">
        ⚠️
      </div>
      <div>
        <h2 className="text-xl font-bold text-white tracking-wide">Membership Expired</h2>
        <p className="text-gray-400 text-sm mt-1">
          Your membership expired on <strong className="text-red-400 font-semibold">{formatDate(expiryDate)}</strong>.
        </p>
        <p className="text-gray-500 text-xs mt-2 max-w-xs mx-auto leading-relaxed">
          Please contact your gym administration to renew your membership and regain facility access.
        </p>
      </div>

      <div className="flex flex-col w-full gap-3 max-w-xs pt-2">
        {rawPhone && (
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 bg-[#6CD800] hover:bg-[#5bc200] text-[#0B0F19] font-black text-xs uppercase tracking-wider rounded-lg transition-all shadow-md shadow-[#6CD800]/10 no-underline cursor-pointer"
          >
            💬 Renew via WhatsApp
          </a>
        )}

        {rawPhone && (
          <a
            href={callUrl}
            className="flex items-center justify-center gap-2 w-full py-3 bg-[#181818] border border-[#2b2b2b] hover:bg-[#222] text-white font-bold text-xs uppercase tracking-wider rounded-lg transition-all no-underline cursor-pointer"
          >
            📞 Call Gym
          </a>
        )}
      </div>
    </div>
  );
};

export default QRCodeContent;